import { useQuery } from "@tanstack/react-query"
import { notFound, useNavigate, useParams, Link as LinkT } from "@tanstack/react-router"
import { getPublicationByClassicId, getPublications } from "../../api/publications"
import {
    Avatar,
    Box,
    Container,
    Flex,
    Heading,
    Text,
    Image,
    Icon,
    Tag,
    Grid,
    GridItem,
    Link,
    HoverCard,
    IconButton,
    Portal,
    Clipboard,
    useBreakpointValue
} from "@chakra-ui/react"
import { useEffect, useState } from "react"
import { Prose } from "../UI/prose"
import {
    AuthorFkPreviewNameOnly,
    CategoryPublic,
    PublicationBlockAuthorPublic,
    PublicationBlockBrissagoPublic,
    PublicationBlockCarouselImagePublic,
    PublicationBlockCarouselPublic,
    PublicationBlockGalleryImagePublic,
    PublicationBlockGalleryPublic,
    PublicationBlockImagePublic,
    PublicationBlockLinkPublic,
    PublicationBlockPublic,
    PublicationBlockTextPublic,
    PublicationBlockVideoPublic
} from "../../client"
import AuthorBlock from "../Publications/AuthorBlock"
import TextBlock from "../Publications/TextBlock"
import LinkBlock from "../Publications/LinkBlock"
import VideoBlock from "../Publications/VideoBlock"
import BrissagoBlock from "../Publications/BrissagoBlock"
import ImageBlock from "../Publications/ImageBlock"
import CarouselBlock from "../Publications/CarouselBlock"
import GalleryBlock from "../Publications/GalleryBlock"
import { FaRegCalendarDays, FaWhatsapp } from "react-icons/fa6"
import NewsletterComponent from "../Common/Newsletter"
import PublicationPreview from "./PublicationPreview"

import jakob from "../../../public/assets/images/jakobmarkt-inserat.jpg"
import { FiFacebook, FiMail, FiShare2 } from "react-icons/fi"
import { Helmet } from "react-helmet-async"
import { scrollToTop } from "../../utils"


const Publication = () => {
    const { slug, classicId } = useParams({ strict: false })
    const navigate = useNavigate()

    const id = classicId ?? slug
    const mainUrl = window.location.origin
    const fullUrl = `${mainUrl}/${id}`


    const {data: publication, isFetched} = useQuery({
        ...getPublicationByClassicId(Number(id)),
        enabled: !!id,
        retry: false,
    })

    if (isFetched && !publication) throw notFound()

    const [lastBlockAuthor, setLastBlock] = useState<boolean>()
    const [firstCat, setFirstCat] = useState<CategoryPublic>()
    const [leadImage, setLeadImage] = useState<PublicationBlockCarouselImagePublic | PublicationBlockImagePublic | PublicationBlockGalleryImagePublic>()
    const [leadBlock, setLeadBlock] = useState<PublicationBlockPublic>()
    const [leadCaption, setLeadCaption] = useState<string | null>()
    const [leadIsArchive, setLeadIsArchive] = useState<boolean>()
    const [leadAuthor, setLeadAuthor] = useState<AuthorFkPreviewNameOnly | null>()

    useEffect(() => {
        setLeadImage(undefined)
        setLeadBlock(undefined)
        setLeadCaption(undefined)
        setLeadIsArchive(undefined)
        setLeadAuthor(null)

        const len = publication?.fk_blocks?.length ?? 0
        const block = publication?.fk_blocks?.[len - 1]

        if (block && block.block_type === "author") setLastBlock(true)

        setFirstCat(publication?.fk_categories[0])

        // Get lead image from first block with image (no image needed if only a gallery block is present)
        const lead =
            publication?.fk_blocks?.[0]?.block_type === "gallery"
                ? null
                : publication?.fk_blocks?.find(b => ["image", "gallery", "carousel"].includes(b.block_type))

        const shouldSkipLead =
            lead &&
            !["gallery", "carousel"].includes(lead.block_type)

        if (lead) {
            switch (lead.block_type) {
                case "image":
                    setLeadImage(lead.block_data as PublicationBlockImagePublic)
                    setLeadCaption((lead.block_data as PublicationBlockImagePublic).caption )
                    setLeadIsArchive((lead.block_data as PublicationBlockImagePublic).is_archive_image)
                    setLeadAuthor((lead.block_data as PublicationBlockImagePublic).fk_author)
                    break
                case "gallery":
                    setLeadImage((lead.block_data as PublicationBlockGalleryPublic).fk_gallery_images[0])
                    break
                case "carousel":
                    setLeadImage((lead.block_data as PublicationBlockCarouselPublic).fk_carousel_images[0])
                    setLeadCaption((lead.block_data as PublicationBlockCarouselPublic).fk_carousel_images[0].caption )
                    setLeadAuthor((lead.block_data as PublicationBlockCarouselPublic).fk_carousel_images[0].fk_author)
                    break
            }
        }

        if (shouldSkipLead) {
            setLeadBlock(lead)
        } else {
            setLeadBlock(undefined)
        }
    }, [publication])

    const { data: morePublications } = useQuery({
        queryKey: ["publications", firstCat],
        queryFn: () =>
            getPublications({ category_id: firstCat?.id!, skip: 0 }).queryFn(),
        enabled: !!firstCat,
        select: (result) => ({
            ...result,
            data: result.data.filter(pub => pub.id !== publication?.id)
        })
    })

    useEffect(() => {
        scrollToTop()
    }, [id])

    const linkText = `Schau dir diesen Beitrag der Wochen-Zeitung an: ${fullUrl}`

    const isMobile = useBreakpointValue({ base: true, md: false })
    const [hovercardOpen, setHovercardOpen] = useState(false)

    return (
        publication &&
            <>
                <Helmet>
                    <title>{publication.title}</title>
                    <meta property="og:title" content={publication.title} />
                    {publication.lead &&
                        <meta property="og:description" content={publication.lead} />
                    }
                    {leadImage &&
                        <meta property="og:image" content={`${mainUrl}${leadImage?.fk_media?.path}`} />
                    }
                    <meta property="og:url" content={fullUrl} />
                    <meta property="og:type" content="article" />
                </Helmet>

                <Container p={{sm: 4, md: 0}}>
                    <Box textAlign="end">
                        <HoverCard.Root 
                            size="sm" 
                            openDelay={200} 
                            open={isMobile ? hovercardOpen : undefined} 
                            onInteractOutside={() => isMobile && setHovercardOpen(false)}
                        >
                            <HoverCard.Trigger bgColor="white" borderRadius="full" p={1} onClick={() => isMobile && setHovercardOpen(!hovercardOpen)}>
                                <IconButton as={FiShare2} size="xs" bgColor="transparent" color="main" />
                            </HoverCard.Trigger>

                            <Portal>
                                <HoverCard.Positioner>
                                    <HoverCard.Content>
                                        <HoverCard.Arrow />
                                        <Flex>
                                            <Clipboard.Root value={fullUrl} mr={2}>
                                                <Clipboard.Trigger asChild borderRadius="full" bgColor="main" color="secondary">
                                                    <IconButton variant="surface" size="sm">
                                                        <Clipboard.Indicator />
                                                    </IconButton>
                                                </Clipboard.Trigger>
                                            </Clipboard.Root>

                                            <Link
                                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`}
                                                target="_blank"
                                                bgColor="main"
                                                borderRadius="full"
                                                mx={2}
                                            >
                                                <IconButton
                                                    as={FiFacebook}
                                                    size="sm"
                                                    bgColor="main"
                                                    color="secondary"
                                                    borderRadius="full"
                                                    p={2}
                                                />
                                            </Link>

                                            <Link
                                                href={`https://wa.me/?text=${encodeURIComponent(linkText)}`}
                                                target="_blank"
                                                bgColor="main"
                                                borderRadius="full"
                                                mx={2}
                                            >
                                                <IconButton
                                                    as={FaWhatsapp}
                                                    size="sm"
                                                    bgColor="main"
                                                    color="secondary"
                                                    borderRadius="full"
                                                    p={2}
                                                />
                                            </Link>

                                            <Link
                                                href={`mailto:?subject=${encodeURIComponent(publication.title)}&body=${encodeURIComponent(linkText)}`}
                                                bgColor="main"
                                                borderRadius="full"
                                                ml={2}
                                            >
                                                <IconButton
                                                    as={FiMail}
                                                    size="sm"
                                                    bgColor="main"
                                                    color="secondary"
                                                    borderRadius="full"
                                                    p={2}
                                                />
                                            </Link>
                                        </Flex>
                                    </HoverCard.Content>
                                </HoverCard.Positioner>
                            </Portal>
                        </HoverCard.Root>
                    </Box>

                    {/* Title */}
                    <Heading size="4xl" color="main">{publication.title}</Heading>

                    {/* Subtitle */}
                    {publication.subtitle &&
                        <Box
                            backgroundColor="color-mix(in srgb, var(--main) 50%, transparent)"
                            w="fit-content"
                            px={2}
                            py={1}
                            mt={10}
                            borderLeft="5px solid"
                            borderColor="main"
                        >
                            <Text fontWeight="bold">{publication.subtitle}</Text>
                        </Box>
                    }

                    {publication.lead &&
                        <Text mt={4}>{publication.lead}</Text>
                    }

                    {leadImage &&
                        <Flex my={10} justifyContent="center">
                            <Flex flexDirection="column" width="fit-content">
                                <Image 
                                    maxH="50em" 
                                    src={isMobile ? leadImage?.fk_media?.path.replace(/-(LG)\./, "-MD.") : leadImage?.fk_media?.path} 
                                    alt={leadImage?.fk_media?.file_name} 
                                />

                                {leadCaption &&
                                    <Text fontSize={15}>{leadCaption}</Text>
                                }

                                {leadAuthor &&
                                    <Text color="main" fontSize={15}>
                                        Bild{`${leadIsArchive ? " (Archiv)" : ""}`}: {`${leadAuthor.firstname ?? ""} ${leadAuthor.lastname ?? ""} ${leadAuthor.abbreviation ? `(${leadAuthor.abbreviation})` : ""}`}
                                    </Text>
                                }
                            </Flex>
                        </Flex>
                    }

                    {/* Content */}
                    {publication.content &&
                        <Prose
                            my={10}
                            w="full"
                            maxW="none"
                            css={{
                                "& > p:first-of-type": {
                                    margin: 0,
                                },
                                "& a": {
                                    color: "main",
                                    textDecor: "none",
                                },
                            }}
                            color="black"
                            dangerouslySetInnerHTML={{ __html: publication.content }}
                        />
                    }

                    {/* Auhtor blocks */}
                    {publication.fk_blocks?.map((block) => {
                        //Skip lead image block
                        if(leadBlock && block.order_number == leadBlock.order_number) return null

                        switch(block.block_type) {
                            case "author":
                                return <AuthorBlock key={block.order_number} data={block.block_data as PublicationBlockAuthorPublic} />
                            case "text":
                                return <TextBlock key={block.order_number} data={block.block_data as PublicationBlockTextPublic} />
                            case "video":
                                return <VideoBlock key={block.order_number} data={block.block_data as PublicationBlockVideoPublic} />
                            case "brissago":
                                return <BrissagoBlock key={block.order_number} data={block.block_data as PublicationBlockBrissagoPublic} />
                            case "image":
                                return <ImageBlock key={block.order_number} data={block.block_data as PublicationBlockImagePublic} />
                            case "gallery":
                                return <GalleryBlock key={block.order_number} data={block.block_data as PublicationBlockGalleryPublic} />
                            case "link":
                                return <LinkBlock key={block.order_number} data={block.block_data as PublicationBlockLinkPublic} />
                            case "carousel":
                                return <CarouselBlock key={block.order_number} data={block.block_data as PublicationBlockCarouselPublic} />
                            default:
                                return null
                        }
                    })}

                    {/* Tags */}
                    <Flex my={10} gap={4}>
                        {publication.fk_tags.map((tag) => (
                            <Tag.Root
                                key={tag.id}
                                px={2}
                                py={1}
                                bgColor="main"
                                color="secondary"
                                borderColor="main"
                                size="lg"
                                cursor="pointer"
                                onClick={() =>
                                    navigate({
                                        to: "/suche",
                                        search: {
                                            tagId: tag.id,
                                            tagName: tag.name,
                                        },
                                    })
                                }
                            >
                                <Tag.Label>{tag.name}</Tag.Label>
                            </Tag.Root>
                        ))}
                    </Flex>

                    {/* Publication date */}
                    <Flex alignContent="center" my={10}>
                        <Icon as={FaRegCalendarDays} size="lg" color="main" />
                        <Text ml={4}>
                            {new Intl.DateTimeFormat("de-CH", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                                timeZone: "Europe/Zurich",
                            }).format(new Date(publication.publication_dt))}
                        </Text>
                    </Flex>

                    {/* Authors */}
                    {!lastBlockAuthor &&
                        publication.fk_authors.map((author) => (
                            author.show_profile ? (
                                <LinkT to="/autor/$authorId" params={{ authorId: author.id }}>
                                    <Flex alignItems="center" mr={8} key={author.id} my={10}>
                                        {author.fk_media_profile_pic &&
                                            <Avatar.Root size="md">
                                                <Avatar.Fallback
                                                    name={ author.lastname && author.firstname
                                                        ? `${author.lastname} ${author.firstname}`
                                                        : author.firstname || author.lastname || author.abbreviation || undefined
                                                    }
                                                />
                                                <Avatar.Image src={author?.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
                                            </Avatar.Root>
                                        }

                                        <Text ml={author.fk_media_profile_pic ? 4 : 0} color="main">
                                            {`${author.firstname ?? ""} ${author.lastname ?? ""} ${author.abbreviation ? `(${author.abbreviation})` : ""}`}
                                        </Text>
                                    </Flex>
                                </LinkT>
                            ) : (
                                <Flex alignItems="center" mr={8} key={author.id} my={10}>
                                    {author.fk_media_profile_pic &&
                                        <Avatar.Root size="md">
                                            <Avatar.Fallback
                                                name={ author.lastname && author.firstname
                                                    ? `${author.lastname} ${author.firstname}`
                                                    : author.firstname || author.lastname || author.abbreviation || undefined
                                                }
                                            />
                                            <Avatar.Image src={author?.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
                                        </Avatar.Root>
                                    }

                                    <Text ml={author.fk_media_profile_pic ? 4 : 0} color="main">
                                        {`${author.firstname ?? ""} ${author.lastname ?? ""} ${author.abbreviation ? `(${author.abbreviation})` : ""}`}
                                    </Text>
                                </Flex>
                            )
                        ))
                    }

                    {/* Newsletter */}
                    <NewsletterComponent />

                    {/* More */}
                    <Flex my={10} flexDir="column">
                        <Text mb={4} fontWeight="bold">Weitere Beiträge aus der Kategorie {firstCat?.name}</Text>
                        <Grid
                            templateColumns={{
                                base: "repeat(1, 1fr)",
                                lg: "repeat(2, 1fr)",
                                xl: "repeat(3, 1fr)",
                                "2xl": "repeat(4, 1fr)",
                            }}
                            gap="2em"
                            justifyItems="center"
                            justifyContent="center"
                        >
                            {morePublications?.data?.slice(0, 4).map((pub) => (
                                <GridItem
                                    cursor="pointer"
                                    key={pub.id}
                                    w="full"
                                    h="full"
                                >
                                    <PublicationPreview publication={pub} />
                                </GridItem>
                            ))}
                        </Grid>
                    </Flex>

                    <Flex direction="column" alignItems="center" hideFrom="md" my={32} gap={32}>
                        <Link href="https://www.jakob-markt.ch/jakob-schuehparadies.html" target="_blank" w="100%" cursor="pointer"><Image src={jakob} alt="Jakob's Schuehparadies" /></Link>
                    </Flex>
                </Container>
            </>
    )
}

export default Publication
